/**
 * One-shot voice management. A Voice owns every node it creates, schedules its own stop, and is
 * disconnected by the pool once its last source has ended. Pools cap polyphony by fading out
 * (never hard-cutting) the oldest voice.
 */
import { clamp, disconnect, makePanner, stopSource } from './util';

/** Where a voice (or layer) sends its signal: a dry input and a reverb-send input. */
export interface Route {
  readonly dry: AudioNode;
  readonly wet: AudioNode;
}

export interface VoiceOpts {
  readonly pan?: number;
  /** Reverb send amount (0..1). */
  readonly send?: number;
}

export class Voice {
  /** Voice master — everything the voice makes ends here. Only automated by kill(). */
  readonly out: GainNode;
  readonly t0: number;
  endAt: number;
  killed = false;
  private readonly nodes: AudioNode[] = [];
  private readonly srcs: AudioScheduledSourceNode[] = [];

  constructor(
    readonly ctx: BaseAudioContext,
    t0: number,
    route: Route,
    opts?: VoiceOpts,
  ) {
    this.t0 = t0;
    this.endAt = t0 + 0.05;
    this.out = ctx.createGain();
    this.nodes.push(this.out);
    let tail: AudioNode = this.out;
    const pan = opts?.pan ?? 0;
    if (pan !== 0 && Number.isFinite(pan)) {
      const p = makePanner(ctx);
      if (p) {
        p.pan.value = clamp(pan, -1, 1);
        this.out.connect(p);
        this.nodes.push(p);
        tail = p;
      }
    }
    tail.connect(route.dry);
    const send = opts?.send ?? 0;
    if (send > 0 && Number.isFinite(send)) {
      const s = ctx.createGain();
      s.gain.value = Math.min(1, send);
      tail.connect(s);
      s.connect(route.wet);
      this.nodes.push(s);
    }
  }

  gain(value = 0): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    this.nodes.push(g);
    return g;
  }

  filter(type: BiquadFilterType, freq: number, q = 0.7071): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = Math.max(10, Math.min(freq, this.ctx.sampleRate * 0.49));
    f.Q.value = q;
    this.nodes.push(f);
    return f;
  }

  osc(shape: OscillatorType | PeriodicWave, freq: number): OscillatorNode {
    const o = this.ctx.createOscillator();
    // (PeriodicWave is a structurally empty interface, so narrow explicitly.)
    if (typeof shape === 'string') o.type = shape as OscillatorType;
    else o.setPeriodicWave(shape);
    o.frequency.value = Math.max(0.01, Math.min(freq, this.ctx.sampleRate * 0.49));
    this.nodes.push(o);
    return o;
  }

  src(buffer: AudioBuffer, loop = false, rate = 1): AudioBufferSourceNode {
    const s = this.ctx.createBufferSource();
    s.buffer = buffer;
    s.loop = loop;
    s.playbackRate.value = rate;
    this.nodes.push(s);
    return s;
  }

  /** A panner (or a unity gain when StereoPanner is unavailable). */
  panner(pan = 0): StereoPannerNode | GainNode {
    const p = makePanner(this.ctx);
    if (!p) return this.gain(1);
    p.pan.value = clamp(pan, -1, 1);
    this.nodes.push(p);
    return p;
  }

  /** Start a source at t0, stop it at t1 and extend the voice lifetime accordingly. */
  play(src: AudioScheduledSourceNode, t0: number, t1: number, offset?: number): void {
    const start = Math.max(t0, this.ctx.currentTime);
    const stop = Math.max(start + 0.005, t1);
    // Only buffer sources accept an offset (no instanceof: RN has no global node constructors).
    if (offset !== undefined) (src as AudioBufferSourceNode).start(start, Math.max(0, offset));
    else src.start(start);
    stopSource(src, stop);
    this.srcs.push(src);
    if (stop > this.endAt) this.endAt = stop;
  }

  /** Fast fade (stolen voice / hard stop) without a click. */
  kill(now: number, fade = 0.03): void {
    if (this.killed) return;
    this.killed = true;
    const g = this.out.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(1, now);
    g.linearRampToValueAtTime(0, now + fade);
    const stopAt = now + fade + 0.01;
    for (const s of this.srcs) stopSource(s, stopAt);
    this.endAt = Math.min(this.endAt, stopAt);
  }

  dispose(): void {
    for (const s of this.srcs) stopSource(s);
    for (const n of this.nodes) disconnect(n);
    this.srcs.length = 0;
    this.nodes.length = 0;
  }
}

export class VoicePool {
  private readonly voices: Voice[] = [];

  constructor(
    private readonly ctx: BaseAudioContext,
    readonly cap: number,
  ) {}

  /**
   * Allocate a voice starting at `t`. Over the cap, the live voice closest to finishing (i.e. the
   * quietest tail) is faded out — long pads and drones survive, decaying plucks make room.
   */
  voice(t: number, route: Route, opts?: VoiceOpts): Voice {
    const now = this.ctx.currentTime;
    this.sweep(now);
    let alive = 0;
    let victim: Voice | null = null;
    for (const v of this.voices) {
      if (v.killed || v.endAt <= now) continue;
      alive++;
      if (v.t0 <= now && (!victim || v.endAt < victim.endAt)) victim = v;
    }
    if (alive >= this.cap) {
      if (!victim) for (const v of this.voices) if (!v.killed && (!victim || v.t0 < victim.t0)) victim = v;
      victim?.kill(now);
    }
    const v = new Voice(this.ctx, Math.max(t, now), route, opts);
    this.voices.push(v);
    return v;
  }

  /** Disconnect voices whose sources have all ended. */
  sweep(now: number): void {
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const v = this.voices[i];
      if (v && now > v.endAt + 0.1) {
        v.dispose();
        this.voices.splice(i, 1);
      }
    }
  }

  killAll(now: number, fade = 0.05): void {
    for (const v of this.voices) v.kill(now, fade);
  }

  get size(): number {
    return this.voices.length;
  }

  get active(): number {
    const now = this.ctx.currentTime;
    let n = 0;
    for (const v of this.voices) if (!v.killed && v.endAt > now) n++;
    return n;
  }

  dispose(): void {
    for (const v of this.voices) v.dispose();
    this.voices.length = 0;
  }
}
